import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Database } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  userProfile: Database['public']['Tables']['user_profiles']['Row'] | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  updateUserRole: (role: 'homeowner' | 'contractor') => Promise<void>;
  signOut: () => Promise<void>;
  checkAndCreateProfile: () => Promise<boolean>;
  createCompleteUserProfile: (userId: string, userData: {
    email: string;
    displayName: string;
    avatarUrl?: string;
    role: 'homeowner' | 'contractor';
    isWorker?: boolean;
    additionalData?: any;
  }) => Promise<boolean>;
  refreshProfile: () => Promise<void>;
  isProfileComplete: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<Database['public']['Tables']['user_profiles']['Row'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const { toast } = useToast();

  // Fetch user profile from database
  const fetchUserProfile = async (userId: string) => {
    setLoading(true);
    
    try {
      console.log('Fetching user profile for user:', userId);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
        
      if (error) throw error;
      
      console.log('User profile fetched:', data);
      setUserProfile(data);
      setIsProfileComplete(!!data?.role);
      
      // Update display name from metadata if not set in profile
      if (data && !data.display_name) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && user.user_metadata?.full_name) {
          const { error: updateError } = await supabase
            .from('user_profiles')
            .update({ display_name: user.user_metadata.full_name })
            .eq('id', userId);
          
          if (!updateError) {
            setUserProfile(prev => prev ? {...prev, display_name: user.user_metadata.full_name as string} : prev);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user profile.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Create complete user profile with role-specific records
  const createCompleteUserProfile = async (userId: string, userData: {
    email: string;
    displayName: string;
    avatarUrl?: string;
    role: 'homeowner' | 'contractor';
    isWorker?: boolean;
    additionalData?: any;
  }) => {
    try {
      logger.log('Creating complete user profile for:', userId, userData.role);
      
      // 1. Check if user_profiles record already exists (created by handle_new_user trigger)
      const { data: existingProfile, error: checkError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing profile:', checkError);
        throw checkError;
      }

      if (existingProfile) {
        logger.log('Profile already exists:', {
          currentRole: existingProfile.role,
          targetRole: userData.role,
          needsRoleUpdate: !existingProfile.role
        });
        // Update existing profile with role and other data
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            role: userData.role,
            display_name: userData.displayName,
            avatar_url: userData.avatarUrl || existingProfile.avatar_url,
            is_worker: userData.isWorker || false,
          })
          .eq('id', userId);

        if (updateError) {
          console.error('Error updating user profile:', updateError);
          throw updateError;
        }
        logger.log('User profile updated successfully');
      } else {
        logger.log('No existing profile found, creating new one...');
        // Create new profile (fallback if trigger didn't fire)
        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            id: userId,
            email: userData.email,
            display_name: userData.displayName,
            avatar_url: userData.avatarUrl || null,
            role: userData.role,
            is_worker: userData.isWorker || false,
          });

        if (profileError) {
          console.error('Error creating user profile:', profileError);
          throw profileError;
        }
        logger.log('User profile created successfully');
      }

      // 2. Then create role-specific record
      if (userData.role === 'homeowner') {
        const { error: clientError } = await supabase
          .from('client')
          .insert({
            id: userId,
            name: userData.displayName,
            email: userData.email,
            phone: userData.additionalData?.phone || null,
            profile_picture: userData.avatarUrl || null,
          });

        if (clientError) {
          console.error('Error creating client record:', clientError);
          throw clientError;
        }
        logger.log('Client record created successfully');
        
      } else if (userData.role === 'contractor') {
        const { error: constructorError } = await supabase
          .from('constructor')
          .insert({
            id: userId,
            name: userData.displayName,
            email: userData.email,
            phone: userData.additionalData?.phone || 'Not provided',
            billing_address: userData.additionalData?.company || 'Not provided',
            nif: userData.additionalData?.licenseNumber || `temp-${userId.slice(0, 8)}`,
            profile_picture: userData.avatarUrl || null,
            // Use the normalized specialization_id instead of legacy text
            specialization_id: userData.additionalData?.specialization_id || null,
            total_projects: 0,
            rating: null,
            pct_on_time: null,
            pct_on_budget: null,
            start_year: null,
            license_documents: null,
            verified: false,
            badges: null,
            work_address: userData.additionalData?.work_address || '',
          });

        if (constructorError) {
          console.error('Error creating constructor record:', constructorError);
          throw constructorError;
        }
        logger.log('Constructor record created successfully');
      }

      // 3. Create worker profile if isWorker is true
      if (userData.isWorker) {
        const { error: workerError } = await supabase
          .from('worker_profiles')
          .insert({
            user_id: userId,
            trade_id: null,
            years_experience: null,
            location_text: userData.additionalData?.work_address || null,
            country: null,
            postal_code: null,
            cv_uri: null,
          });

        if (workerError) {
          console.error('Error creating worker profile:', workerError);
          throw workerError;
        }
        logger.log('Worker profile created successfully');
      }

      // 4. Update auth metadata
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { role: userData.role }
      });

      if (metadataError) {
        console.error('Error updating auth metadata:', metadataError);
        // Don't throw here as this is not critical
      }

      logger.log('User profile creation completed successfully');
      
      // 4. Refresh the profile data
      await fetchUserProfile(userId);

      return true;
    } catch (error) {
      console.error('Error creating complete user profile:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to create user profile.';
      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      
      toast({
        title: 'Profile Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
      
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      // Clean up existing auth state
      cleanupAuthState();
      
      // Attempt to sign out first - this prevents auth limbo states
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      
      if (error) throw error;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      toast({
        title: 'Sign In Failed',
        description: 'Unable to sign in with Google. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const signInWithPassword = async (email: string, password: string) => {
    try {
      // Clean up existing auth state
      cleanupAuthState();
      
      // Attempt to sign out first - this prevents auth limbo states
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      // Auth state change will trigger profile fetch
    } catch (error) {
      console.error('Error signing in with password:', error);
      toast({
        title: 'Sign In Failed',
        description: error instanceof Error ? error.message : 'Unable to sign in. Please check your credentials and try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const checkAndCreateProfile = async () => {
    if (!user) return false;
    
    try {
      setLoading(true);
      
      // Check if profile exists
      const { data: existingProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
        
      if (profileError) throw profileError;
      
      // Check if user signed up with Google (no password provider means OAuth)
      const isGoogleUser = user.app_metadata.providers?.includes('google') || false;
      
      if (!existingProfile) {
        logger.log('No existing profile found, creating new one for Google user...');
        // Create new profile (fallback if trigger didn't fire)
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            id: user.id,
            email: user.email || '',
            display_name: user.user_metadata.full_name as string || user.email?.split('@')[0] || '',
            avatar_url: user.user_metadata.avatar_url as string || null,
            // Automatically set Google users as homeowners
            role: isGoogleUser ? 'homeowner' : null,
          });
          
        if (insertError) throw insertError;
        
        // If it's a Google user, also create client record and update auth metadata
        if (isGoogleUser) {
          await supabase.auth.updateUser({
            data: { role: 'homeowner' }
          });
          
          // Create client record
          const { error: clientError } = await supabase
            .from('client')
            .insert({
              id: user.id,
              name: user.user_metadata.full_name as string || user.email?.split('@')[0] || 'User',
              email: user.email || '',
              phone: null,
              profile_picture: user.user_metadata.avatar_url as string || null
            });
          
          if (clientError && clientError.code !== '23505') {
            console.error('Error creating client record for Google user:', clientError);
          }
        }
        
        // Fetch the newly created profile
        await fetchUserProfile(user.id);
        
        // Return true if profile is complete (Google users), false otherwise
        return isGoogleUser;
      } else {
        // Profile exists, check if it needs role assignment for Google users
        if (isGoogleUser && !existingProfile.role) {
          logger.log('Google user profile exists but no role assigned, updating...');
          
          // Update existing profile with homeowner role
          const { error: updateError } = await supabase
            .from('user_profiles')
            .update({
              role: 'homeowner',
              display_name: user.user_metadata.full_name as string || existingProfile.display_name,
              avatar_url: user.user_metadata.avatar_url as string || existingProfile.avatar_url,
            })
            .eq('id', user.id);

          if (updateError) {
            console.error('Error updating Google user profile with role:', updateError);
            throw updateError;
          }

          // Update auth metadata
          await supabase.auth.updateUser({
            data: { role: 'homeowner' }
          });

          // Create client record if it doesn't exist
          const { data: existingClient } = await supabase
            .from('client')
            .select('id')
            .eq('id', user.id)
            .maybeSingle();

          if (!existingClient) {
            const { error: clientError } = await supabase
              .from('client')
              .insert({
                id: user.id,
                name: user.user_metadata.full_name as string || user.email?.split('@')[0] || 'User',
                email: user.email || '',
                phone: null,
                profile_picture: user.user_metadata.avatar_url as string || null
              });

            if (clientError && clientError.code !== '23505') {
              console.error('Error creating client record for Google user:', clientError);
            }
          }

          // Fetch the updated profile
          await fetchUserProfile(user.id);
          
          logger.log('Google user role assignment completed');
          return true;
        }
        
        // Check if profile is complete (has a role)
        return !!existingProfile.role;
      }
      
    } catch (error) {
      console.error('Error checking/creating profile:', error);
      toast({
        title: 'Profile Error',
        description: 'Failed to retrieve or create your profile. Please try again.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (role: 'homeowner' | 'contractor') => {
    if (!user) return;
    
    try {
      logger.log('Updating user role to:', role);
      
      // First, update user metadata
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { role }
      });
      
      if (metadataError) throw metadataError;
      
      // Then update the user_profiles table
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ role })
        .eq('id', user.id);
        
      if (profileError) throw profileError;

      // Create role-specific record in appropriate table
      if (role === 'homeowner') {
        // Check if client record already exists
        const { data: existingClient } = await supabase
          .from('client')
          .select('id')
          .eq('id', user.id)
          .maybeSingle();

        if (!existingClient) {
          const { error: clientError } = await supabase
            .from('client')
            .insert({
              id: user.id,
              name: userProfile?.display_name || user.email?.split('@')[0] || 'User',
              email: user.email || '',
              phone: null,
              profile_picture: userProfile?.avatar_url || null
            });
          
          if (clientError && clientError.code !== '23505') { // Ignore duplicate key errors
            console.error('Error creating client record:', clientError);
          }
        }
      } else if (role === 'contractor') {
        // Check if constructor record already exists
        const { data: existingConstructor } = await supabase
          .from('constructor')
          .select('id')
          .eq('id', user.id)
          .maybeSingle();

        if (!existingConstructor) {
          const { error: constructorError } = await supabase
            .from('constructor')
            .insert({
              id: user.id,
              name: userProfile?.display_name || user.email?.split('@')[0] || 'User',
              email: user.email || '',
              phone: '', // Required field, set to empty string for now
              billing_address: '', // Required field, set to empty string for now
              nif: '', // Required field, set to empty string for now
              profile_picture: userProfile?.avatar_url || null,
              // legacy specialization removed in favor of specialization_id
              specialization_id: null,
              total_projects: 0,
              rating: null,
              pct_on_time: null,
              pct_on_budget: null,
              start_year: null,
              license_documents: null,
              verified: false,
              badges: null,
              work_address: '', // Required field, set to empty string for now
            });
          
          if (constructorError && constructorError.code !== '23505') { // Ignore duplicate key errors
            console.error('Error creating constructor record:', constructorError);
          }
        }
      }
      
      // Update local state
      if (userProfile) {
        setUserProfile({ ...userProfile, role });
        setIsProfileComplete(true);
      }
      
      toast({
        title: 'Profile Updated',
        description: `You're now registered as a ${role}.`,
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update your role. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const signOut = async () => {
    try {
      // First clear local state immediately
      setSession(null);
      setUser(null);
      setUserProfile(null);
      setIsProfileComplete(false);
      setLoading(false);
      
      // Clean up auth state
      cleanupAuthState();
      
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
      
      toast({
        title: 'Signed out',
        description: 'You have been signed out successfully.',
      });
      
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'Sign Out Failed',
        description: 'Unable to sign out. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Handle Google user profile creation/update
  const handleGoogleUserProfile = async (authUser: any) => {
    try {
      setLoading(true);
      
      // Check if profile exists
      const { data: existingProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();
        
      if (profileError) throw profileError;
      
      if (!existingProfile) {
        logger.log('No existing profile found, creating new one for Google user...');
        // Create new profile (fallback if trigger didn't fire)
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            id: authUser.id,
            email: authUser.email || '',
            display_name: authUser.user_metadata.full_name as string || authUser.email?.split('@')[0] || '',
            avatar_url: authUser.user_metadata.avatar_url as string || null,
            role: 'homeowner',
          });
          
        if (insertError) throw insertError;
      } else if (!existingProfile.role) {
        logger.log('Google user profile exists but no role assigned, updating...');
        
        // Update existing profile with homeowner role
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            role: 'homeowner',
            display_name: authUser.user_metadata.full_name as string || existingProfile.display_name,
            avatar_url: authUser.user_metadata.avatar_url as string || existingProfile.avatar_url,
          })
          .eq('id', authUser.id);

        if (updateError) {
          console.error('Error updating Google user profile with role:', updateError);
          throw updateError;
        }
      }

      // Update auth metadata
      await supabase.auth.updateUser({
        data: { role: 'homeowner' }
      });

      // Create client record if it doesn't exist
      const { data: existingClient } = await supabase
        .from('client')
        .select('id')
        .eq('id', authUser.id)
        .maybeSingle();

      if (!existingClient) {
        const { error: clientError } = await supabase
          .from('client')
          .insert({
            id: authUser.id,
            name: authUser.user_metadata.full_name as string || authUser.email?.split('@')[0] || 'User',
            email: authUser.email || '',
            phone: null,
            profile_picture: authUser.user_metadata.avatar_url as string || null
          });

        if (clientError && clientError.code !== '23505') {
          console.error('Error creating client record for Google user:', clientError);
        }
      }

      // Fetch the updated profile
      await fetchUserProfile(authUser.id);
      
      logger.log('Google user role assignment completed');
      
    } catch (error) {
      console.error('Error handling Google user profile:', error);
      toast({
        title: 'Profile Error',
        description: 'Failed to set up your Google account. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper to clean up auth state to prevent "limbo" states
  const cleanupAuthState = () => {
    // Remove standard auth tokens
    localStorage.removeItem('supabase.auth.token');
    // Remove all Supabase auth keys from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    // Remove from sessionStorage if in use
    Object.keys(sessionStorage || {}).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        sessionStorage.removeItem(key);
      }
    });
  };

  // Listen for auth state changes
  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        await fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
        setIsProfileComplete(false);
        setLoading(false);
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.log('Auth state change:', event, session?.user?.id);
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (event === 'SIGNED_OUT') {
          setUserProfile(null);
          setIsProfileComplete(false);
          setLoading(false);
        } else if (session?.user) {
          // For Google OAuth sign-ins, we need to check and potentially create/update the profile
          const isGoogleUser = session.user.app_metadata.providers?.includes('google') || false;
          
          if (isGoogleUser && event === 'SIGNED_IN') {
            logger.log('Google user signed in, checking profile...');
            // Small delay to ensure database operations complete, then check/create profile
            setTimeout(async () => {
              // Call checkAndCreateProfile with the session user directly
              await handleGoogleUserProfile(session.user);
            }, 100);
          } else {
            // For regular users, just fetch the profile
            setTimeout(() => {
              fetchUserProfile(session.user.id);
            }, 100);
          }
        } else {
          setUserProfile(null);
          setIsProfileComplete(false);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const refreshProfile = async () => {
    if (user) {
      console.log('Refreshing user profile for user:', user.id);
      try {
        await fetchUserProfile(user.id);
        console.log('User profile refreshed successfully');
      } catch (error) {
        console.error('Error refreshing user profile:', error);
        throw error;
      }
    } else {
      console.warn('Cannot refresh profile: no user authenticated');
    }
  };

  const contextValue: AuthContextType = {
    session,
    user,
    userProfile,
    loading,
    signInWithGoogle,
    signInWithPassword,
    updateUserRole,
    signOut,
    checkAndCreateProfile,
    createCompleteUserProfile,
    refreshProfile,
    isProfileComplete,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
